import React from 'react';
import { Form2 } from '../primitives/Forms2';
import { useTagsList } from './data/useTagsList';
import { useCRMContext } from './CRMContext';
import { FormControlsForFields, TableForFields } from '../schemed';
import { FormGrid, LoadingIndicator, MoreActionsMenuButton, OccupyFreeSpace, SearchField, SimpleDialog, useWindowHotkey } from '../primitives';
import { IconButton, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { TagEditDrawer } from './TagEditDrawer';
import { CRMMenu } from './Elements/CRMMenu';
import { useProcessQueryParam } from '../../hooks/useProcessQueryParam';
import { TagColorPicker } from './Elements/TagColorPicker';
import { TagChipBare, TagsWrapper } from './Elements/CRMTags';
import { MergeTagsPanel } from './Elements/MergeTagsPanel';

interface Props {}

export const TagsPage = (props: Props) => {
  const ctx = useCRMContext();
  const data = useTagsList(ctx.apiPath);

  useWindowHotkey("alt+n", () => data.newRecord.startEditing());

  useProcessQueryParam("id",
    v => data.findTag(v).then(record => {
      data.editRecord.startEditing(record);
    }),
  );

  return (<>
    <Form2
      noTitle
      background="panel"
      fitFullHeight
      dimmed={data.editRecord.isEditing}
      formHeaderProps={{
        onClick: () => data.editRecord.cancel(),
      }}
      headerItems={<>
        <CRMMenu title="Теги" current="tags" />

        <IconButton size="small" color="primary" onClick={() => data.newRecord.startEditing()}>
          <Add />
        </IconButton>

        <OccupyFreeSpace />

        {data.isLoading && <LoadingIndicator sizeVariant="s" />}

        <Typography variant="caption" color="textSecondary" alignSelf="flex-end" style={{ marginBottom: 4}}>
          {data.count.data.total_records}
        </Typography>

        <SearchField
          filter={data.filter.filter}
          setFilter={data.filter.setFilter}
          noButton
          autoFocus
        />
      </>}
    >
      <TableForFields
        data={data.data}
        fields={[
          ["actions", { label: " ", width: 50 }],
          ["name"],
          ["description"],
        ]}
        fieldElement={f => {
          switch(f) {
            case "name":
              return r => <TagsWrapper><TagChipBare tag={r} /></TagsWrapper>
            case "actions":
              if(data.merge.isPanelVisible) {
                return r => <>
                  {r._id !== data.merge.targetTag?._id &&
                    !data.merge.sourceTags?.find(t => t._id === r._id) &&
                  <IconButton size="small" onClick={e =>{ e.stopPropagation(); data.merge.addSource(r)}}><Add /></IconButton>}
                  
                </>
              } else {
                return r => (
                  <MoreActionsMenuButton
                    size="small"
                    stopPropagation
                    actions={[
                      ["merge", "Слияние", () => data.merge.startMerge(r)]
                    ]}
                    />
                )
              }
          }
        }}
        onRowClick={r => data.editRecord.startEditing({ ...r })}
        schema={data.schema}
        tailAnchor={data.limitTrigger.pagingAnchor}
      />
    </Form2>

    <TagEditDrawer 
      schema={data.schema} 
      editRecord={data.editRecord} 
      removeRecord={data.remove.run}
    />

    <SimpleDialog
      {...data.newRecord.dialog}
      dialogTitle="Новый тег"
      save={() => data.newRecord.save()}
      maxWidth="xs"
      noFullscreen
      submitOnModEnter
    >
      {!!data.newRecord.item &&
        <FormGrid columns="1fr" noMargin>
          <FormControlsForFields
            fields={[
              ["name", { controlProps: { autoFocus: true }}],
            ]}
            schema={data.schema}
            data={data.newRecord.item}
            onChange={(o,c) => data.newRecord.update(c)}
          />
          <TagColorPicker
            value={data.newRecord.item?.color}
            update={v => data.newRecord.update({ color: v as any })}
            />
          
          <FormControlsForFields
            fields={[
              ["description", { controlProps: { multiline: true, rows: 2 } }],
            ]}
            schema={data.schema}
            data={data.newRecord.item}
            onChange={(o,c) => data.newRecord.update(c)}
          />
        </FormGrid>}
    </SimpleDialog>

    <MergeTagsPanel
      merge={data.merge}
      onComplete={() => data.reload()}
      />
  </>);
};
